import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  CalendarDays, Users, Sparkles, ShieldCheck, Loader2, Check, Camera,
  Lock, Mail, Bell, Save, KeyRound, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  site_survey: '#C9A84C', installation: '#60a5fa', maintenance: '#2dd4bf',
  cleaning: '#c084fc', meeting: '#fb923c', other: '#9ca3af',
};
const statusColors = {
  scheduled: 'bg-indigo-500/10 text-indigo-400',
  completed: 'bg-green-500/10 text-green-500',
  cancelled: 'bg-red-500/10 text-red-400',
  pending: 'bg-yellow-500/10 text-yellow-500',
  approved: 'bg-green-500/10 text-green-500',
  rejected: 'bg-red-500/10 text-red-400',
};

const Card = ({ children, className = '', ...props }) => (
  <div className={`bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm ${className}`} {...props}>{children}</div>
);

const Empty = ({ text, testId }) => (
  <Card className="p-8 text-center text-white/30 font-manrope text-sm" data-testid={testId}>{text}</Card>
);

/* ======================== APPOINTMENTS ======================== */
export function AppointmentsSection({ headers }) {
  const [events, setEvents] = useState([]);
  const [visits, setVisits] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/client/appointments`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/client/visits`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/client/subscriptions`, { headers }).catch(() => ({ data: [] })),
    ]).then(([e, v, s]) => { setEvents(e.data); setVisits(v.data); setSubs(s.data); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={22} className="text-[#C9A84C] animate-spin" /></div>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.date >= today && e.status !== 'cancelled');
  const past = events.filter(e => e.date < today || e.status === 'cancelled');

  return (
    <div data-testid="portal-appointments">
      <p className="text-white/30 font-manrope text-sm mb-6">Your scheduled appointments and service visit history.</p>

      {/* Plan visit progress */}
      {subs.filter(s => s.visits_per_year > 0 && s.status === 'active').length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="visit-progress-cards">
          {subs.filter(s => s.visits_per_year > 0 && s.status === 'active').map(s => {
            const pct = Math.min(100, Math.round(((s.visits_completed || 0) / s.visits_per_year) * 100));
            return (
              <Card key={s.id} className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white font-manrope text-sm font-semibold">{s.package_name}</p>
                  <span className="text-[#C9A84C] font-manrope text-xs font-bold">{s.visits_completed || 0}/{s.visits_per_year} visits</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#C9A84C] rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-white/25 font-manrope text-[10px] mt-2 uppercase tracking-widest">{s.visits_remaining ?? 0} visit{(s.visits_remaining ?? 0) !== 1 ? 's' : ''} remaining this year</p>
              </Card>
            );
          })}
        </div>
      )}

      <h3 className="font-playfair text-white text-lg mb-4">Upcoming</h3>
      {upcoming.length === 0 ? (
        <Empty text="No upcoming appointments — we'll post them here once scheduled" testId="no-upcoming-appointments" />
      ) : (
        <div className="space-y-3 mb-8">
          {upcoming.map(e => (
            <Card key={e.id} className="p-4 flex items-center gap-4" data-testid={`appointment-${e.id}`}>
              <div className="w-12 h-12 rounded-sm flex flex-col items-center justify-center border" style={{ borderColor: `${CATEGORY_COLORS[e.category] || '#9ca3af'}44`, backgroundColor: `${CATEGORY_COLORS[e.category] || '#9ca3af'}11` }}>
                <CalendarDays size={16} style={{ color: CATEGORY_COLORS[e.category] || '#9ca3af' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-manrope text-sm font-semibold truncate">{e.title}</p>
                <p className="text-white/35 font-manrope text-xs">{e.date}{e.time ? ` · ${e.time}` : ''}{e.staff_names?.length ? ` · with ${e.staff_names.join(', ')}` : ''}</p>
              </div>
              <span className="px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold" style={{ color: CATEGORY_COLORS[e.category] || '#9ca3af', backgroundColor: `${CATEGORY_COLORS[e.category] || '#9ca3af'}15` }}>
                {(e.category || 'other').replace('_', ' ')}
              </span>
            </Card>
          ))}
        </div>
      )}

      {(past.length > 0 || visits.length > 0) && (
        <>
          <h3 className="font-playfair text-white text-lg mb-4">Visit History</h3>
          <div className="space-y-3">
            {visits.map(v => (
              <Card key={v.id} className="p-4" data-testid={`visit-history-${v.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white font-manrope text-sm font-semibold">{v.package_name || 'Service Visit'}</p>
                  <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${statusColors[v.status] || 'bg-white/5 text-white/40'}`}>{v.status}</span>
                </div>
                <p className="text-white/35 font-manrope text-xs mb-2">{v.date}{v.time ? ` · ${v.time}` : ''}</p>
                {v.notes && <p className="text-white/45 font-manrope text-xs leading-relaxed">{v.notes}</p>}
                {v.correction_notes && <p className="text-yellow-500/70 font-manrope text-xs mt-1.5 border-l-2 border-yellow-500/40 pl-2">Follow-up: {v.correction_notes}</p>}
                {(v.photos || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {v.photos.map((p, i) => (
                      <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-sm overflow-hidden border border-[#c9a84c]/15">
                        <img src={`${API}/files/${p}`} alt={`Visit ${i + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </Card>
            ))}
            {visits.length === 0 && past.length > 0 && past.map(e => (
              <Card key={e.id} className="p-4 flex items-center justify-between" data-testid={`past-appointment-${e.id}`}>
                <div>
                  <p className="text-white/70 font-manrope text-sm">{e.title}</p>
                  <p className="text-white/30 font-manrope text-xs">{e.date}{e.time ? ` · ${e.time}` : ''}</p>
                </div>
                <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${statusColors[e.status] || 'bg-white/5 text-white/40'}`}>{e.status}</span>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ======================== MY TEAM ======================== */
export function TeamSection({ headers }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/client/team`, { headers }).then(r => setTeam(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={22} className="text-[#C9A84C] animate-spin" /></div>;

  return (
    <div data-testid="portal-team">
      <p className="text-white/30 font-manrope text-sm mb-6">Your dedicated Dream Room Theaters representatives.</p>
      {team.length === 0 ? (
        <div className="flex flex-col items-center py-16" data-testid="no-team">
          <div className="w-16 h-16 rounded-full bg-[#1A0F12] border border-[#c9a84c]/10 flex items-center justify-center mb-5">
            <Users size={24} className="text-[#C9A84C]/30" />
          </div>
          <h3 className="font-playfair text-white/50 text-lg mb-2">No Team Assigned Yet</h3>
          <p className="text-white/25 font-manrope text-sm text-center max-w-sm">Your dedicated representatives will appear here once your project begins.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {team.map(m => (
            <Card key={m.id} className="p-6 flex items-center gap-4" data-testid={`team-member-${m.id}`}>
              <div className="w-14 h-14 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
                <span className="font-playfair text-[#C9A84C] text-xl font-bold">{m.name?.charAt(0) || '?'}</span>
              </div>
              <div className="min-w-0">
                <h4 className="font-playfair text-white text-base truncate">{m.name}</h4>
                <p className="text-[#C9A84C] font-manrope text-[10px] uppercase tracking-widest mb-1">{m.title || (m.role || '').replace('_', ' ')}</p>
                <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 text-white/40 hover:text-[#C9A84C] font-manrope text-xs transition-colors truncate">
                  <Mail size={11} /> {m.email}
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ======================== CLEANING ======================== */
export function CleaningSection({ headers, setActiveSection }) {
  const [subs, setSubs] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/client/subscriptions`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/client/visits`, { headers }).catch(() => ({ data: [] })),
    ]).then(([s, v]) => { setSubs(s.data.filter(x => x.category === 'cleaning')); setVisits(v.data.filter(x => (x.package_id || '').startsWith('cleaning'))); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={22} className="text-[#C9A84C] animate-spin" /></div>;

  return (
    <div data-testid="portal-cleaning">
      <p className="text-white/30 font-manrope text-sm mb-6">Professional theater cleaning — plans, visits, and history.</p>

      {subs.length === 0 ? (
        <Card className="p-8 text-center mb-6" data-testid="no-cleaning-plan">
          <Sparkles size={26} className="text-[#C9A84C]/40 mx-auto mb-4" />
          <h3 className="font-playfair text-white text-lg mb-2">No Active Cleaning Plan</h3>
          <p className="text-white/30 font-manrope text-sm mb-5 max-w-sm mx-auto">Keep your theater in pristine condition with a professional cleaning plan performed by trained luxury-space specialists.</p>
          <button onClick={() => setActiveSection('services')} className="bg-[#C9A84C] hover:bg-[#D4B560] text-black px-6 py-2.5 rounded-sm font-manrope font-bold text-xs uppercase tracking-widest" data-testid="browse-cleaning-plans-btn">
            Browse Cleaning Plans
          </button>
        </Card>
      ) : (
        <div className="space-y-4 mb-8">
          {subs.map(s => {
            const pct = s.visits_per_year ? Math.min(100, Math.round(((s.visits_completed || 0) / s.visits_per_year) * 100)) : 0;
            return (
              <Card key={s.id} className="p-5" data-testid={`cleaning-sub-${s.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Sparkles size={16} className="text-[#C9A84C]" />
                    <p className="text-white font-manrope text-sm font-semibold">{s.package_name}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${s.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-white/5 text-white/40'}`}>{s.status}</span>
                </div>
                <p className="text-white/30 font-manrope text-xs mb-3">${s.amount}/{s.billing_period === 'annual' ? 'yr' : 'mo'}{s.next_billing_date ? ` · next billing ${s.next_billing_date.slice(0, 10)}` : ''}</p>
                {s.visits_per_year > 0 && (
                  <>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#C9A84C] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-white/25 font-manrope text-[10px] mt-2 uppercase tracking-widest">{s.visits_completed || 0} of {s.visits_per_year} visits used · {s.visits_remaining ?? 0} remaining</p>
                  </>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {visits.length > 0 && (
        <>
          <h3 className="font-playfair text-white text-lg mb-4">Cleaning Visit History</h3>
          <div className="space-y-3">
            {visits.map(v => (
              <Card key={v.id} className="p-4" data-testid={`cleaning-visit-${v.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white/70 font-manrope text-sm">{v.date}{v.time ? ` · ${v.time}` : ''}</p>
                  <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${statusColors[v.status] || 'bg-white/5 text-white/40'}`}>{v.status}</span>
                </div>
                {v.notes && <p className="text-white/40 font-manrope text-xs">{v.notes}</p>}
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ======================== WARRANTY BLOCK ======================== */
export function WarrantyBlock({ headers }) {
  const [regs, setRegs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ theater_name: '', install_date: '', invoice_number: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/client/warranty`, { headers }).then(r => setRegs(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.install_date) return;
    setSaving(true);
    try {
      await axios.post(`${API}/client/warranty/register`, form, { headers });
      setForm({ theater_name: '', install_date: '', invoice_number: '', address: '', notes: '' });
      setShowForm(false);
      load();
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  const inputCls = 'w-full bg-transparent border-b border-white/20 focus:border-[#C9A84C] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm';

  return (
    <div className="mb-8" data-testid="warranty-block">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#C9A84C]" />
          <h3 className="font-playfair text-white text-lg">Warranty Registration</h3>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="bg-[#C9A84C] hover:bg-[#D4B560] text-black px-4 py-2 rounded-sm font-manrope font-bold text-[10px] uppercase tracking-widest" data-testid="register-warranty-btn">
            Register My Theater
          </button>
        )}
      </div>

      {showForm && (
        <Card className="p-5 mb-4">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input placeholder="Theater name (e.g. Smith Family Cinema)" value={form.theater_name} onChange={e => setForm({ ...form, theater_name: e.target.value })} className={inputCls} data-testid="warranty-name-input" />
              <div>
                <label className="text-[9px] uppercase tracking-widest text-white/30 font-manrope block">Installation date</label>
                <input type="date" required value={form.install_date} onChange={e => setForm({ ...form, install_date: e.target.value })} className={inputCls} data-testid="warranty-date-input" />
              </div>
              <input placeholder="Invoice / contract number (optional)" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} className={inputCls} data-testid="warranty-invoice-input" />
              <input placeholder="Installation address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className={inputCls} data-testid="warranty-address-input" />
            </div>
            <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="warranty-notes-input" />
            <div className="flex gap-3">
              <button type="submit" disabled={saving} className="bg-[#C9A84C] hover:bg-[#D4B560] text-black px-6 py-2 rounded-sm font-manrope font-bold text-xs uppercase tracking-widest disabled:opacity-50" data-testid="submit-warranty-btn">
                {saving ? 'Submitting...' : 'Submit Registration'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="text-white/40 font-manrope text-xs uppercase tracking-widest px-6 py-2 border border-white/10 rounded-sm">Cancel</button>
            </div>
          </form>
        </Card>
      )}

      {regs.length > 0 ? (
        <div className="space-y-3">
          {regs.map(r => (
            <Card key={r.id} className="p-4 flex items-center justify-between" data-testid={`warranty-reg-${r.id}`}>
              <div>
                <p className="text-white font-manrope text-sm font-semibold">{r.theater_name || 'My Theater'}</p>
                <p className="text-white/30 font-manrope text-xs">Installed {r.install_date}{r.invoice_number ? ` · Invoice ${r.invoice_number}` : ''}</p>
              </div>
              <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${statusColors[r.status] || 'bg-white/5 text-white/40'}`} data-testid={`warranty-status-${r.id}`}>{r.status}</span>
            </Card>
          ))}
        </div>
      ) : !showForm && (
        <Card className="p-5 text-white/30 font-manrope text-xs leading-relaxed">
          Register your theater installation to activate warranty coverage for defects in materials and workmanship. Registration takes less than a minute.
        </Card>
      )}
    </div>
  );
}

/* ======================== ACCOUNT SETTINGS ======================== */
export function AccountSettingsSection({ profile, reloadProfile, headers, isActivated, uanCode, setUanCode, activateUAN, uanLoading, uanError }) {
  const [form, setForm] = useState({ name: profile?.name || '', phone: profile?.phone || '' });
  const [prefs, setPrefs] = useState(profile?.notification_prefs || { email_updates: true, appointment_reminders: true, promo_emails: true });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    setForm({ name: profile?.name || '', phone: profile?.phone || '' });
    if (profile?.notification_prefs) setPrefs(profile.notification_prefs);
  }, [profile]);

  const flash = (msg) => { setSavedMsg(msg); setTimeout(() => setSavedMsg(''), 2500); };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.patch(`${API}/client/profile`, { name: form.name, phone: form.phone, notification_prefs: prefs }, { headers });
      flash('Saved!');
      reloadProfile();
    } catch (err) { console.error(err); } finally { setSavingProfile(false); }
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await axios.patch(`${API}/client/profile`, { notification_prefs: next }, { headers }); } catch (err) { console.error(err); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/client/profile/photo`, fd, { headers });
      reloadProfile();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
      if (photoRef.current) photoRef.current.value = '';
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pw.new_password !== pw.confirm) { setPwMsg({ ok: false, text: 'New passwords do not match' }); return; }
    setPwSaving(true);
    try {
      await axios.post(`${API}/client/change-password`, { current_password: pw.current_password, new_password: pw.new_password }, { headers });
      setPwMsg({ ok: true, text: 'Password updated successfully' });
      setPw({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      const d = err.response?.data?.detail;
      setPwMsg({ ok: false, text: typeof d === 'string' ? d : 'Failed to update password' });
    } finally { setPwSaving(false); }
  };

  const photoUrl = profile?.profile_picture_url ? (profile.profile_picture_url.startsWith('http') ? profile.profile_picture_url : `${process.env.REACT_APP_BACKEND_URL}${profile.profile_picture_url}`) : null;
  const inputCls = 'w-full bg-transparent border-b border-white/20 focus:border-[#C9A84C] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm';
  const labelCls = 'text-[9px] uppercase tracking-widest text-white/30 font-manrope block mb-1';

  return (
    <div data-testid="portal-account-settings" className="max-w-2xl space-y-6">
      {/* Profile card */}
      <Card className="p-6">
        <div className="flex items-center gap-5 mb-6">
          <div className="relative group">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center">
              {photoUrl ? (
                <img src={photoUrl} alt={profile?.name} className="w-full h-full object-cover" data-testid="profile-photo" />
              ) : (
                <span className="font-playfair text-[#C9A84C] text-2xl font-bold">{profile?.name?.charAt(0) || 'C'}</span>
              )}
            </div>
            <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#C9A84C] flex items-center justify-center cursor-pointer hover:bg-[#D4B560] transition-colors" title="Change photo" data-testid="upload-photo-btn">
              {uploadingPhoto ? <Loader2 size={12} className="animate-spin text-black" /> : <Camera size={12} className="text-black" />}
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
            </label>
          </div>
          <div>
            <h3 className="font-playfair text-white text-lg">{profile?.name}</h3>
            <p className="text-white/40 font-manrope text-sm">{profile?.email}</p>
            <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-sm text-[9px] uppercase tracking-widest font-manrope font-bold ${isActivated ? 'bg-green-500/10 text-green-500' : 'bg-[#C9A84C]/10 text-[#C9A84C]'}`}>
              {isActivated ? 'Tier 2 — Full Project Access' : 'Tier 1 — Standard Access'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <label className={labelCls}>Full Name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="settings-name-input" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="settings-phone-input" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={saveProfile} disabled={savingProfile} className="flex items-center gap-2 bg-[#C9A84C] hover:bg-[#D4B560] text-black px-5 py-2 rounded-sm font-manrope font-bold text-xs uppercase tracking-widest disabled:opacity-50" data-testid="save-profile-btn">
            {savingProfile ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Changes
          </button>
          {savedMsg && <span className="text-green-500 font-manrope text-xs flex items-center gap-1" data-testid="profile-saved-msg"><Check size={12} /> {savedMsg}</span>}
        </div>

        {profile?.uan && (
          <div className="flex justify-between items-center py-2 border-t border-[#c9a84c]/10 mt-5 pt-4">
            <span className="text-white/30 font-manrope text-xs uppercase tracking-widest">Your UAN</span>
            <span className="text-[#C9A84C] font-manrope text-sm font-mono font-bold" data-testid="profile-uan">{profile.uan}</span>
          </div>
        )}
      </Card>

      {/* Notification preferences */}
      <Card className="p-6" data-testid="notification-prefs">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={15} className="text-[#C9A84C]" />
          <h4 className="font-playfair text-white text-base">Notification Preferences</h4>
        </div>
        <div className="space-y-3">
          {[
            { key: 'email_updates', label: 'Project & account updates' },
            { key: 'appointment_reminders', label: 'Appointment reminders' },
            { key: 'promo_emails', label: 'Promotions & special offers' },
          ].map(p => (
            <button key={p.key} onClick={() => togglePref(p.key)} className="w-full flex items-center justify-between py-2 group" data-testid={`pref-${p.key}`}>
              <span className="text-white/50 font-manrope text-sm group-hover:text-white/70 transition-colors">{p.label}</span>
              <span className={`relative w-9 h-5 rounded-full transition-colors ${prefs[p.key] ? 'bg-[#C9A84C]' : 'bg-white/10'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${prefs[p.key] ? 'left-[18px]' : 'left-0.5'}`} />
              </span>
            </button>
          ))}
        </div>
      </Card>

      {/* Change password */}
      <Card className="p-6" data-testid="change-password-card">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={15} className="text-[#C9A84C]" />
          <h4 className="font-playfair text-white text-base">Change Password</h4>
        </div>
        <form onSubmit={changePassword} className="space-y-4">
          <input type="password" required placeholder="Current password" value={pw.current_password} onChange={e => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="current-password-input" autoComplete="current-password" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input type="password" required minLength={8} placeholder="New password (min 8 chars)" value={pw.new_password} onChange={e => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="new-password-input" autoComplete="new-password" />
            <input type="password" required placeholder="Confirm new password" value={pw.confirm} onChange={e => setPw({ ...pw, confirm: e.target.value })} className={inputCls} data-testid="confirm-password-input" autoComplete="new-password" />
          </div>
          {pwMsg && <p className={`font-manrope text-xs ${pwMsg.ok ? 'text-green-500' : 'text-red-400'}`} data-testid="password-msg">{pwMsg.text}</p>}
          <button type="submit" disabled={pwSaving} className="flex items-center gap-2 bg-[#C9A84C] hover:bg-[#D4B560] text-black px-5 py-2 rounded-sm font-manrope font-bold text-xs uppercase tracking-widest disabled:opacity-50" data-testid="change-password-btn">
            {pwSaving ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} Update Password
          </button>
        </form>
      </Card>

      {/* UAN activation */}
      {!isActivated && (
        <Card className="p-6 border-[#C9A84C]/20" data-testid="uan-activation">
          <h4 className="font-playfair text-white text-base mb-2">Unlock Project Access</h4>
          <p className="text-white/30 font-manrope text-xs mb-4">Have a project activation code from your Dream Room technician or booking confirmation email? Enter it here to unlock full project features.</p>
          {uanError && <p className="text-red-400 font-manrope text-xs mb-3">{uanError}</p>}
          <div className="flex gap-2">
            <input value={uanCode} onChange={e => setUanCode(e.target.value)} placeholder="DRT-XXXX-XXXX"
              className="flex-1 bg-transparent border-b border-white/20 focus:border-[#C9A84C] focus:outline-none px-0 py-2 text-white font-manrope text-sm font-mono placeholder:text-white/15 uppercase tracking-widest" data-testid="uan-input" />
            <button onClick={activateUAN} disabled={uanLoading || !uanCode} className="bg-[#C9A84C] hover:bg-[#D4B560] text-black px-6 py-2 rounded-sm font-manrope font-bold text-xs uppercase tracking-widest disabled:opacity-30" data-testid="activate-uan-btn">
              {uanLoading ? '...' : 'Activate'}
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}
